import { HttpApiService } from "@/shared/services/httpApi.service";
import { readApiMessages, readApiStatus } from "@/shared/utils/apiError";

/**
 * Turns a `coloca-nest` error into something a form can bind.
 *
 * The API gives us less to work with than it looks: **there is no per-field error object and no error-code
 * taxonomy** on the users endpoints. `message` is a `string[]` when the `ValidationPipe` rejected the body
 * (one sentence per broken rule) and a plain `string` for every hand-thrown `BadRequest` / `NotFound` /
 * `Conflict`. So field binding is done by matching the leading token of each sentence against the form's
 * keys, and the few hand-thrown messages that matter are recognised by their text.
 *
 * Two shapes that would otherwise be mis-parsed:
 *
 * - The whitelist rejection is `property <name> should not exist`, where the field is the SECOND token;
 * - `phone_number`'s validator message is written in Spanish, but still opens with the property name.
 */

export interface NormalizedApiError {
  /** One sentence to show when nothing bound to a field. */
  message: string;
  /** Field name → message, for the fields this form owns. */
  fieldErrors: Record<string, string>;
  status: number | null;
  /**
   * A 500 carrying nothing but Nest's generic body — i.e. an exception no handler caught.
   *
   * Decided here because it is the only place that still holds the raw error: once normalised, a message the
   * API never sent (axios's own "Request failed with status code 500") is indistinguishable from one it did.
   */
  isUnhandledServerError: boolean;
}

/** Nest's default body for an unhandled exception. Carries no information beyond the status itself. */
const GENERIC_SERVER_ERROR = /^internal server error\.?$/i;

/**
 * `property foo should not exist` → `foo`; otherwise the first token.
 *
 * Lower-cased because `@IsStrongPassword` is the one validator on these DTOs whose message opens with a
 * capitalised word ("Password must be 8-128 characters…") instead of the property name — so a case-sensitive
 * match would drop it into the unbound bucket and the field would never highlight.
 */
function fieldOf(sentence: string): string | null {
  const tokens = sentence.trim().split(/\s+/);
  if (tokens.length === 0) return null;
  if (tokens[0] === "property" && tokens.length > 1) return tokens[1]?.toLowerCase() ?? null;
  return tokens[0]?.toLowerCase() ?? null;
}

/**
 * @param knownFields The form's field names. Only these are bound; anything else falls into `message`, so an
 *   error about a field the user cannot see is never swallowed into an input they cannot fix.
 */
export function normalizeUserApiError(
  error: unknown,
  knownFields: readonly string[] = [],
): NormalizedApiError {
  const status = readApiStatus(error);
  const messages = readApiMessages(error);
  const fieldErrors: Record<string, string> = {};
  const unbound: string[] = [];

  for (const sentence of messages) {
    const field = fieldOf(sentence);
    if (field && knownFields.includes(field) && !fieldErrors[field]) {
      fieldErrors[field] = sentence;
    } else {
      unbound.push(sentence);
    }
  }

  // The two hand-thrown messages this screen can actually provoke, bound by text because they carry no code.
  const joined = messages.join(" ");
  if (status === 409 && /already exists/i.test(joined) && knownFields.includes("email")) {
    fieldErrors.email ??= joined;
  }
  if (status === 400 && /organization_id/i.test(joined) && knownFields.includes("organization_id")) {
    fieldErrors.organization_id ??= joined;
  }

  // `message` must never come back empty: a caller that toasts it would fire an invisible toast, and a
  // caller that renders an alert would render nothing at all. When every sentence bound to a field, fall
  // back to the first one — it is still a true statement about what failed.
  //
  // `getErrorMessage` is reached only when there were no messages: it does `data.message || error.message`,
  // and `ApiError.message` is typed `string` while the wire can carry a `string[]`, so on a validation
  // failure it would hand back the array itself.
  const message = unbound.join(" ") || messages[0] || HttpApiService.getErrorMessage(error);

  const isUnhandledServerError =
    status === 500 && messages.every((sentence) => GENERIC_SERVER_ERROR.test(sentence.trim()));

  return { message, fieldErrors, status, isUnhandledServerError };
}

/**
 * The address is already taken — bind OUR translated sentence, not the API's.
 *
 * `normalizeUserApiError` binds the server's own message, which is written in English and quotes the address
 * back at the operator. Rule 6 says what the user reads is translated, so the drawers swap it for
 * `error_email_taken` whenever this is true.
 */
export function isEmailConflict(error: NormalizedApiError): boolean {
  return error.status === 409 && Boolean(error.fieldErrors.email);
}

/**
 * Recreating a soft-deleted user's email does not answer 409.
 *
 * The service pre-checks with `findOne`, which TypeORM scopes to `deleted_at IS NULL`, so the address passes
 * the check and the insert then violates `UNIQUE (email)` — a constraint with no `deleted_at` predicate. That
 * surfaces as an uncaught `QueryFailedError`, i.e. a bare 500.
 *
 * **This can only ever be a hint, and the copy it drives says so.** A 500 carries no error code, so an
 * unreachable database produces a byte-identical response; the two are not distinguishable from here. What
 * the check CAN do is refuse to claim anything when the 500 arrived with a real message — the
 * unique-constraint violation escapes unhandled and therefore always carries Nest's generic body.
 *
 * Takes the NORMALISED error, which is what the drawers hold. It used to take `unknown` and re-read the
 * status off an axios error — but the only call site passes a `NormalizedApiError`, which is not one, so
 * `readStatus` answered `null` and the branch was dead: the hint never rendered at all.
 */
export function isProbableDeletedEmailReuse(error: NormalizedApiError): boolean {
  return error.isUnhandledServerError;
}
